import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import WebsiteBuilder from "./components/WebsiteBuilder";
import PublicWebsite from "./components/PublicWebsite";
import ClientWizard from "./components/ClientWizard";
import TemplateManager from "./components/TemplateManager";
import TenantDashboard from "./components/TenantDashboard";
import SaaSAdminPanel from "./components/saas/SaaSAdminPanel";
import BillingSuccess from "./components/BillingSuccess";
import BillingPortal from "./components/BillingPortal";
import LoginPage from "./components/auth/LoginPage";
import ForgotPasswordPage from "./components/auth/ForgotPasswordPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import { LoginRouter, ForgotPasswordRouter, ResetPasswordRouter } from "./components/auth/LoginRouter";
import AcceptInvitePage from "./components/auth/AcceptInvitePage";
import UserManagement from "./components/settings/UserManagement";
import ImpersonationBanner from "./components/ImpersonationBanner";
import ParentPortal from "./components/daycare/ParentPortal";
// eslint-disable-next-line no-unused-vars
import PublicEnrollmentForm from "./components/daycare/PublicEnrollmentForm";
import DaycarePublicHome from "./components/daycare/DaycarePublicHome";
import { TenantProvider, useTenant } from "./contexts/TenantContext";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/* ==========================================
   PLATFORM ROUTES (no tenant prefix)
   ========================================== */
function PlatformRoutes() {
  return (
    <Routes>
      {/* Super Admin SaaS Panel */}
      <Route path="/admin/saas/*" element={<SaaSAdminPanel />} />
      <Route path="/saas-admin/*" element={<SaaSAdminPanel />} />
      
      {/* Admin Routes */}
      <Route path="/admin/templates" element={<TemplateManager />} />
      <Route path="/admin/create-client" element={<ClientWizard />} />
      <Route path="/admin/website-builder" element={<WebsiteBuilder />} />
      
      {/* Billing */}
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing" element={<BillingPortal />} />
      
      {/* Preview */}
      <Route path="/preview" element={<PublicWebsite isPreview={true} />} />
      
      {/* Default — Landing / Wizard */}
      <Route path="/" element={<ClientWizard />} />
    </Routes>
  );
}

/* ==========================================
   TENANT ROUTES (under /:tenant_slug/*)
   ========================================== */
function TenantRoutes() {
  const { tenant_slug } = useParams();

  return (
    <TenantProvider tenantSlug={tenant_slug}>
      <TenantRoutesInner tenantSlug={tenant_slug} />
    </TenantProvider>
  );
}

function TenantRoutesInner({ tenantSlug }) {
  const { loading } = useTenant();

  if (loading) {
    return (
      <div className="prem-auth-page" data-testid="tenant-loading">
        <div className="prem-auth-loading-spinner" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Auth */}
      <Route path="/login" element={<LoginRouter />} />
      <Route path="/forgot-password" element={<ForgotPasswordRouter />} />
      <Route path="/reset-password" element={<ResetPasswordRouter />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      {/* Public daycare marketing (no auth) — homepage + alias routes */}
      <Route path="/enroll" element={<DaycarePublicHome />} />
      <Route path="/website" element={<DaycarePublicHome />} />
      <Route path="/tour" element={<DaycarePublicHome />} />
      <Route path="/contact" element={<DaycarePublicHome />} />

      {/* Tenant Dashboard & CRM */}
      <Route path="/dashboard" element={<TenantDashboard />} />

      {/* Parent Portal */}
      <Route path="/parent/*" element={<ParentPortal />} />

      {/* Settings */}
      <Route path="/settings/users" element={<UserManagement />} />

      {/* Billing */}
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing" element={<BillingPortal />} />

      {/* Website Builder */}
      <Route path="/admin/website-builder" element={<WebsiteBuilder />} />

      {/* Default: tenant root → public marketing homepage */}
      <Route path="/" element={<DaycarePublicHome />} />
      <Route path="/*" element={<Navigate to={`/${tenantSlug}/login`} replace />} />
    </Routes>
  );
}

/* ==========================================
   APP — Route dispatcher
   ========================================== */
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors duration={2000} closeButton />
      <BrowserRouter>
        <ImpersonationBanner />
        <Routes>
          {/* Platform routes (static paths — matched first by specificity) */}
          <Route path="/admin/*" element={<PlatformRoutes />} />
          <Route path="/saas-admin/*" element={<SaaSAdminPanel />} />
          <Route path="/billing/*" element={<PlatformRoutes />} />
          <Route path="/preview" element={<PublicWebsite isPreview={true} />} />

          {/* Tenant path-based routes: /{tenant_slug}/* */}
          <Route path="/:tenant_slug/*" element={<TenantRoutes />} />

          {/* Platform landing */}
          <Route path="/" element={<ClientWizard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
